// frontend/src/features/equipment/EquipmentSetupWizard/types.ts

/**
 * Value for brand/model selection.
 * Supports both catalog selections and "Other" free-text entries.
 */
export interface BrandModelValue {
  brandId?: string;
  brandName?: string;  // For "Other" brand (free text)
  modelId?: string;
  modelName?: string;  // For "Other" model (free text)
}

/**
 * Equipment item being collected during wizard flow.
 * Uses tempId for UI tracking before actual creation.
 */
export interface PendingEquipment {
  /** Temporary UUID for UI tracking */
  tempId: string;
  /** Subcategory ID from equipment catalog */
  subcategoryId: string;
  /** Brand and model selection */
  brandModel: BrandModelValue;
  /** Number of this item (default 1) */
  quantity: number;
  /** If quantity > 1, should each be tracked as separate equipment records? */
  trackSeparately: boolean;
}

/**
 * User's answer to "Do you have this equipment?" question.
 */
export type HasEquipmentAnswer = 'yes' | 'no' | 'not_yet';

/**
 * Wizard state managed by WizardContext.
 */
export interface WizardState {
  /** Whether the wizard modal is open */
  isOpen: boolean;
  /** Current step index (0 = welcome, 1-5 = categories, 6 = review) */
  currentStep: number;
  /** Equipment items collected during wizard */
  pendingEquipment: PendingEquipment[];
  /** Category IDs that user explicitly skipped */
  skippedCategories: string[];
  /** Category IDs already saved in this wizard flow */
  savedCategories: string[];
  /** Category IDs where user has preexisting equipment */
  preexistingCategories: string[];
  /** Equipment entries saved during this wizard session */
  sessionSavedEquipment: PendingEquipment[];
}

/**
 * A single wizard step definition.
 */
export interface WizardStep {
  /** Unique step identifier */
  id: string;
  /** Display label for the step */
  label: string;
  /** Category ID from equipment catalog (undefined for welcome/review) */
  categoryId?: string;
}

/**
 * All wizard steps in order.
 * Step 0 = Welcome, Steps 1-5 = Category steps, Step 6 = Review
 */
export const WIZARD_STEPS: WizardStep[] = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'circulation', label: 'Circulation', categoryId: 'circulation' },
  { id: 'lighting', label: 'Lighting', categoryId: 'lighting' },
  { id: 'filtration', label: 'Filtration', categoryId: 'filtration' },
  { id: 'control', label: 'Control', categoryId: 'control' },
  { id: 'other', label: 'Other', categoryId: 'other' },
  { id: 'review', label: 'Review' },
];

/**
 * Initial wizard state.
 */
export const INITIAL_WIZARD_STATE: WizardState = {
  isOpen: false,
  currentStep: 0,
  pendingEquipment: [],
  skippedCategories: [],
  savedCategories: [],
  preexistingCategories: [],
  sessionSavedEquipment: [],
};
